import { useForm } from "react-hook-form";

import { useErrors } from "@auth0/auth0-acul-react";

import { CloseButton } from "@/components/CloseButton";
import { ULThemeFloatingLabelField, ULThemeFormMessage } from "@/components/form";
import { Form, FormField, FormItem } from "@/components/ui/form";
import { ULThemeButton } from "@/components/ULThemeButton";
import ULThemeCard from "@/components/ULThemeCard";
import { ULThemeAlert, ULThemeAlertTitle } from "@/components/ULThemeError";
import ULThemeLogo from "@/components/ULThemeLogo";
import ULThemePageLayout from "@/components/ULThemePageLayout";
import ULThemeTitle from "@/components/ULThemeTitle";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSubmitLock } from "@/hooks/useSubmitLock";
import { cn } from "@/lib/utils";

import daLocales from "./locales/da.json";
import deLocales from "./locales/de.json";
import enLocales from "./locales/en.json";
import frLocales from "./locales/fr.json";
import nlLocales from "./locales/nl.json";
import noLocales from "./locales/no.json";
import svLocales from "./locales/sv.json";

type FormValues = { code: string };

const localeMap = {
  da: daLocales.mfaOtpCodeInputStep,
  de: deLocales.mfaOtpCodeInputStep,
  en: enLocales.mfaOtpCodeInputStep,
  fr: frLocales.mfaOtpCodeInputStep,
  nl: nlLocales.mfaOtpCodeInputStep,
  no: noLocales.mfaOtpCodeInputStep,
  sv: svLocales.mfaOtpCodeInputStep,
};

export type MfaOtpCodeInputLocaleCode = keyof typeof localeMap;

interface MfaOtpCodeInputStepProps {
  onSubmit: (code: string) => Promise<void>;
  onBack: () => void;
  localeCode: MfaOtpCodeInputLocaleCode;
  cancelUrl: string | undefined;
}

export function MfaOtpCodeInputStep({
  onSubmit,
  onBack,
  localeCode,
  cancelUrl,
}: MfaOtpCodeInputStepProps) {
  const locales = localeMap[localeCode];
  const { isHotelsOrg } = useOrganizationContext();
  const form = useForm<FormValues>({ defaultValues: { code: "" } });
  const { isLoading, guard } = useSubmitLock();
  const { errors, dismiss } = useErrors();
  const authErrors = errors.byType("auth0");

  const handleSubmit = ({ code }: FormValues) =>
    guard(() => onSubmit(code)).catch(() => {
      // Failure is already surfaced to the user via the error banner.
    });

  const handleBack = () => {
    authErrors.forEach((error) => dismiss(error.id));
    onBack();
  };

  return (
    <ULThemePageLayout className="theme-universal mfa-theme">
      <ULThemeCard
        className={cn(
          "relative w-full sm:max-w-(--ul-theme-widget-max-width) gap-0",
          isHotelsOrg && "hotels-theme",
        )}
      >
        <CloseButton href={cancelUrl} />
        <ULThemeLogo altText={locales.logoAltText} />
        <ULThemeTitle className={isHotelsOrg ? "theme-universal:text-primary-button" : undefined}>
          {locales.title}
        </ULThemeTitle>
        <p className="text-body-text text-(length:--ul-theme-font-body-text-size) font-body mb-10 text-center">
          {locales.description}
        </p>
        {authErrors.length > 0 && (
          <div className="space-y-3 mb-8">
            {authErrors.map((error) => (
              <ULThemeAlert
                key={error.id}
                variant="destructive"
                onDismiss={() => dismiss(error.id)}
              >
                <ULThemeAlertTitle>{error.message}</ULThemeAlertTitle>
              </ULThemeAlert>
            ))}
          </div>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4 mt-2">
            <FormField
              control={form.control}
              name="code"
              rules={{ required: locales.codeRequired }}
              render={({ field, fieldState }) => (
                <FormItem>
                  <ULThemeFloatingLabelField
                    {...field}
                    label={locales.codeLabel}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    error={!!fieldState.error}
                    staticLabel
                  />
                  <ULThemeFormMessage hasFormError={!!fieldState.error} />
                </FormItem>
              )}
            />
            <ULThemeButton
              type="submit"
              className="w-full"
              aria-label={locales.submitButton}
              isLoading={isLoading}
            >
              {locales.submitButton}
            </ULThemeButton>
          </form>
        </Form>
        <ULThemeButton
          type="button"
          variant="link"
          size="link"
          className="mt-4"
          aria-label={locales.backButton}
          onClick={handleBack}
        >
          {locales.backButton}
        </ULThemeButton>
      </ULThemeCard>
    </ULThemePageLayout>
  );
}
